// App.jsx
'use strict';
import React from 'react';
import { Outlet, ScrollRestoration } from 'react-router';
import styled, { createGlobalStyle } from 'styled-components';
import styledNormalize from 'styled-normalize';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';

import { localeConvertor } from '../resource/i18nCommon.js';

import media from '../style/media.js';
import {
  footerNavigationHeight,
  iPhoneXSeriesFooterPaddingInPWA,
} from '../style/variables.js';
import ModalView from '../container/ModalView.js';
import ModalsLoader from '../container/ModalsLoader.js';
import LeakerBanWarning from '../component/LeakerBanWarning.jsx';
import SkipHydration from '../component/SkipHydration.jsx';
import UniversalComponent from '../component/UniversalComponent.jsx';
import ChunkLoading from '../component/ChunkLoading.jsx';
import GeneralErrorToastr from '../component/GeneralErrorToastr.jsx';
import RegExpRoute from '../component/RegExpRoute.jsx';
import MultiPathRoute from '../component/MultiPathRoute.jsx';
import { addvalue as addvalueModals } from '../resource/modalId.js';
import { getIsIphoneXSeriesPWA } from '../resource/getUserAgent.js';
import { SEARCH_MODAL_ID } from '../resource/searchConstants.js';
import {
  login as loginModal,
  register as registerModal,
} from '../resource/modalId.js';
import {
  BOTTOM_NAVIGATION_PATHS,
  FOOTER_PATHS,
} from '../resource/getShouldShowFooter.js';
import { ModalId } from '../resource/mixpanel.js';
import {
  landingRegExp,
  ageVerifierRegExp,
  addvalueRegExp,
} from '../resource/pathRegexList.js';
import { generateLangAwarePaths } from '../resource/langPath.js';

const universalConfig = {
  loading: <ChunkLoading />,
  error: <GeneralErrorToastr />,
};

const GlobalStyle = createGlobalStyle`
  ${styledNormalize};
  *, ::after, ::before { box-sizing: border-box; }
  html {
    &::-webkit-scrollbar {
      display: none;
    }
    :root {
      --safe-area-bottom: env(safe-area-inset-bottom, ${iPhoneXSeriesFooterPaddingInPWA}px);
      --space-to-pinned-notification: 0px;
      --space-to-pinned-notification-even-on-desktop: 0px;
    }
  }
  body {
    background-color: #191919;
    color: #fff;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue",
      Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol";
    font-weight: 400;
    -webkit-tap-highlight-color: transparent;
    overscroll-behavior-y: contain;
    &::-webkit-scrollbar {
      display: none;
    }
  }
  button {
    background-color: rgba(0, 0, 0, 0);
    cursor: pointer;
    &:disabled {
      cursor: not-allowed;
    }
  }
  input::placeholder,
  input::-webkit-input-placeholder,
  input::-moz-placeholder {
    line-height: normal !important;
    vertical-align: middle;
  }
  a { color: inherit; text-decoration: none; }
  a > * { opacity: inherit; }
  a:hover {
    opacity: .7;

    ${media.mobile`
      opacity: 1;
    `};
  }
  iframe {
    border-width: 0px;
  }

  ${media.tablet`
    html { touch-action: manipulation; }
  `};
`;

const ClientContainer = props => {
  // eslint-disable-next-line react/prop-types
  const { filename } = props;

  return (
    <SkipHydration
      filename={filename}
      render={() => <UniversalComponent {...props} />}
    />
  );
};

const App = () => {
  const { i18n } = useTranslation();
  const lang = localeConvertor({ locale: i18n.language, isBCP47: true });

  return (
    <StyledApp>
      <Helmet htmlAttributes={{ lang }} />
      <GlobalStyle />
      <ScrollRestoration getKey={location => location.pathname} />
      {/* RemoteConfigMonitor should be stay before and components which would modify url.
          ex: url loaders and history modifier */}
      <UniversalComponent
        filename="Container_RemoteConfigMonitor"
        importer={
          /* #__LOADABLE__ */ () =>
            import('../container/RemoteConfigMonitor.js')
        }
      />
      <ClientContainer
        filename="Container_GtmLoader"
        importer={/* #__LOADABLE__ */ () => import('../container/GtmLoader.js')}
      />
      <ClientContainer
        filename="Container_HistoryModifier"
        importer={
          /* #__LOADABLE__ */ () => import('../container/HistoryModifier.js')
        }
      />
      <ClientContainer
        filename="Container_UrlOAuthLoginLoader"
        importer={
          /* #__LOADABLE__ */ () =>
            import('../container/UrlOAuthLoginLoader.js')
        }
      />
      <ClientContainer
        filename="Container_UrlOtpLoginLoader"
        importer={
          /* #__LOADABLE__ */ () => import('../container/UrlOtpLoginLoader.js')
        }
      />
      <ClientContainer
        filename="Container_UrlPopupLoader"
        importer={
          /* #__LOADABLE__ */ () => import('../container/UrlPopupLoader.js')
        }
      />
      <ClientContainer
        filename="Container_UrlOrderLoader"
        importer={
          /* #__LOADABLE__ */ () => import('../container/UrlOrderLoader.js')
        }
      />
      <ClientContainer
        filename="Container_MonitorLocationChange"
        importer={
          /* #__LOADABLE__ */ () =>
            import('../container/MonitorLocationChange.js')
        }
      />
      <ClientContainer
        filename="Container_CustomerServiceScriptLoader"
        importer={
          /* #__LOADABLE__ */ () =>
            import('../container/CustomerServiceScriptLoader.js')
        }
        timeoutMsec={10000}
      />
      <ClientContainer
        filename="Container_PusherEmitter"
        importer={
          /* #__LOADABLE__ */ () => import('../container/PusherEmitter.js')
        }
      />
      <ClientContainer
        filename="Container_MixpanelChores"
        importer={
          /* #__LOADABLE__ */ () => import('../container/MixpanelChores.js')
        }
      />
      <ClientContainer
        filename="Container_PersistMiddlewareLoader"
        importer={
          /* #__LOADABLE__ */ () =>
            import('../container/PersistMiddlewareLoader.js')
        }
      />
      <ClientContainer
        filename="Container_MediaEngagementMonitor"
        importer={
          /* #__LOADABLE__ */ () =>
            import('../container/MediaEngagementMonitor.js')
        }
      />
      <ClientContainer
        filename="Container_PingCheckHandler"
        importer={
          /* #__LOADABLE__ */ () => import('../container/PingCheckHandler.js')
        }
      />
      <ClientContainer
        filename="Container_ServiceWorkerChores"
        importer={
          /* #__LOADABLE__ */ () =>
            import('../container/ServiceWorkerChores.js')
        }
      />
      <ClientContainer
        filename="Container_PageVisibilityHandler"
        importer={
          /* #__LOADABLE__ */ () =>
            import('../container/PageVisibilityHandler.js')
        }
      />
      <ClientContainer
        filename="Container_DeviceSizeHandler"
        importer={
          /* #__LOADABLE__ */ () => import('../container/DeviceSizeHandler.js')
        }
      />
      <ClientContainer
        filename="Container_UserSessionTick"
        importer={
          /* #__LOADABLE__ */ () => import('../container/UserSessionTick.js')
        }
      />
      <ClientContainer
        filename="Container_ResumableGuardian"
        importer={
          /* #__LOADABLE__ */ () => import('../container/ResumableGuardian.js')
        }
      />
      <ClientContainer
        filename="Container_CaptchaScriptLoader"
        importer={
          /* #__LOADABLE__ */ () =>
            import('../container/CaptchaScriptLoader.js')
        }
      />
      <ClientContainer
        filename="Container_GetRemoteConfigDataWithTimeout"
        importer={
          /* #__LOADABLE__ */ () =>
            import('../container/GetRemoteConfigDataWithTimeout.js')
        }
      />
      <ClientContainer
        filename="Container_IframeProtector"
        importer={
          /* #__LOADABLE__ */ () => import('../container/IframeProtector.js')
        }
      />
      <ClientContainer
        filename="Container_PushNotificationHandler"
        importer={
          /* #__LOADABLE__ */ () =>
            import('../container/PushNotificationHandler.js')
        }
      />
      <ClientContainer
        filename="Container_PollingPusherChannelData"
        importer={
          /* #__LOADABLE__ */ () =>
            import('../container/PollingPusherChannelData.js')
        }
      />
      <ClientContainer
        filename="Container_DebugTracking"
        importer={
          /* #__LOADABLE__ */ () => import('../container/DebugTracking.js')
        }
      />
      <ClientContainer
        filename="Container_StorageCleaner"
        importer={
          /* #__LOADABLE__ */ () => import('../container/StorageCleaner.js')
        }
      />
      <ClientContainer
        filename="Container_WebVitalsLoader"
        importer={
          /* #__LOADABLE__ */ () => import('../container/WebVitalsLoader.js')
        }
      />
      <ClientContainer
        filename="Container_HotjarLoader"
        importer={
          /* #__LOADABLE__ */ () => import('../container/HotjarLoader.js')
        }
      />
      <ClientContainer
        filename="Container_WebVersionDetector"
        importer={
          /* #__LOADABLE__ */ () => import('../container/WebVersionDetector.js')
        }
      />
      <ClientContainer
        filename="Container_PhoneVerifyRewardHandler"
        importer={
          /* #__LOADABLE__ */ () =>
            import('../container/PhoneVerifyRewardHandler.js')
        }
      />
      <ClientContainer
        filename="Container_PopupNotificationList"
        importer={
          /* #__LOADABLE__ */ () =>
            import('../container/PopupNotificationList.js')
        }
      />
      <LeakerBanWarning />
      {/* Can't just use .*livestream. Otherwise, some feeds or leaderboard might be filtered out. */}
      <RegExpRoute
        regExp={
          /^(?!\/user\/[^/]+\/livestream|\/age-verify|\/join|\/revenue).*/i
        }
        element={
          <ClientContainer
            filename="Container_AgeVerificationPinnedNotification"
            importer={
              /* #__LOADABLE__ */ () =>
                import('../container/AgeVerificationPinnedNotification.js')
            }
          />
        }
      />
      <RegExpRoute
        regExp={landingRegExp}
        element={
          <UniversalComponent
            filename="Container_Landing"
            importer={
              /* #__LOADABLE__ */ () => import('../container/Landing.js')
            }
          />
        }
      />
      <RegExpRoute
        regExp={ageVerifierRegExp}
        element={
          <UniversalComponent
            filename="Container_AgeVerifierModal"
            importer={
              /* #__LOADABLE__ */ () =>
                import('../container/AgeVerifierModal.js')
            }
          />
        }
      />
      <RegExpRoute
        regExp={/^\/(?!404)/i}
        element={
          <>
            <ClientContainer
              filename="Container_FlightCheck"
              importer={
                /* #__LOADABLE__ */ () => import('../container/FlightCheck.js')
              }
            />
            <ClientContainer
              filename="Container_UrlQueryLoader"
              importer={
                /* #__LOADABLE__ */ () =>
                  import('../container/UrlQueryLoader.js')
              }
            />
          </>
        }
      />
      <RegExpRoute
        regExp={addvalueRegExp}
        element={
          <ModalsLoader ids={Object.values(addvalueModals)}>
            <UniversalComponent
              filename="Container_AddvalueModals"
              importer={
                /* #__LOADABLE__ */ () =>
                  import('../container/AddvalueModals.js')
              }
            />
          </ModalsLoader>
        }
      />
      <MultiPathRoute
        paths={BOTTOM_NAVIGATION_PATHS}
        element={
          <UniversalComponent
            filename="Container_NavFooter"
            {...universalConfig}
            importer={
              /* #__LOADABLE__ */ () => import('../container/NavFooter.js')
            }
          />
        }
      />
      <MultiPathRoute
        paths={generateLangAwarePaths(['/', '/chat', '/chat/:chatroomId'])}
        element={
          <ClientContainer
            filename="Container_NotificationReadList"
            importer={
              /* #__LOADABLE__ */ () =>
                import('../container/NotificationReadList.js')
            }
          />
        }
      />

      {/* FloatingActionButtons */}
      <ClientContainer
        filename="Container_FloatingActionButtons"
        importer={
          /* #__LOADABLE__ */ () =>
            import('../container/FloatingActionButtons.js')
        }
      >
        <MultiPathRoute
          paths={generateLangAwarePaths([
            '/',
            '/following',
            '/video',
            '/feed',
            '/chat',
          ])}
          element={
            <ClientContainer
              filename="Container_UserEventEntranceButton"
              importer={
                /* #__LOADABLE__ */ () =>
                  import('../container/UserEventEntranceButton.js')
              }
            />
          }
        />
        <MultiPathRoute
          paths={generateLangAwarePaths([
            '/',
            '/following',
            '/video',
            '/story',
            '/feed',
            '/chat',
          ])}
          element={
            <ClientContainer
              filename="Container_BackpackEntranceButton"
              importer={
                /* #__LOADABLE__ */ () =>
                  import('../container/BackpackEntranceButton.js')
              }
            />
          }
        />
        <MultiPathRoute
          paths={generateLangAwarePaths([
            '/',
            '/following',
            '/video',
            '/story',
            '/chat',
            '/settings',
            '/user/:userId',
            '/user/:userId/:listType',
            '/user/:userId/:listType/:publishStatus',
            '/u/:username',
            '/u/:username/:listType',
            '/u/:username/:listType/:publishStatus',
            '/join/:progress',
            '/age-verify/:progress',
          ])}
          element={
            <ClientContainer
              filename="Container_CustomerServiceButton"
              importer={
                /* #__LOADABLE__ */ () =>
                  import('../container/CustomerServiceButton.js')
              }
            />
          }
        />
      </ClientContainer>
      {/* FloatingActionButtons */}

      <Outlet />

      <MultiPathRoute
        paths={FOOTER_PATHS}
        element={
          <UniversalComponent
            filename="Container_Footer"
            importer={
              /* #__LOADABLE__ */ () => import('../container/Footer.js')
            }
          />
        }
      />

      <UniversalComponent
        filename="Container_NeedAuth"
        importer={/* #__LOADABLE__ */ () => import('../container/NeedAuth.js')}
      >
        <UniversalComponent
          data-key="authed"
          filename="Container_OpenNotificationAsker"
          importer={
            /* #__LOADABLE__ */ () =>
              import('../container/OpenNotificationAsker.js')
          }
        />
      </UniversalComponent>
      <RegExpRoute
        regExp={/^\/(?!join|revenue)/i}
        element={
          <ModalView
            id="GeneralUrlPopup"
            noScrollLock
            render={() => (
              <UniversalComponent
                filename="Container_GeneralUrlPopup"
                importer={
                  /* #__LOADABLE__ */ () =>
                    import('../container/GeneralUrlPopup.js')
                }
              />
            )}
          />
        }
      />
      <RegExpRoute
        regExp={/^\/(?!join|revenue)/i}
        element={
          <ModalView
            id="NotificationAsker"
            noScrollLock
            render={() => (
              <UniversalComponent
                filename="Container_NotificationAsker"
                importer={
                  /* #__LOADABLE__ */ () =>
                    import('../container/NotificationAsker.js')
                }
              />
            )}
          />
        }
      />
      <RegExpRoute
        regExp={/^\/(?!join|revenue)/i}
        element={
          <ModalView
            id="PwaInstaller"
            noScrollLock
            render={() => (
              <UniversalComponent
                filename="Container_PwaInstaller"
                importer={
                  /* #__LOADABLE__ */ () =>
                    import('../container/PwaInstaller.js')
                }
              />
            )}
          />
        }
      />
      <RegExpRoute
        regExp={/^\/(?!age-verify)/i}
        element={
          <ModalView
            id={registerModal.REGISTER_EMAIL}
            modalId={ModalId.Bind.BindEmailEnter}
            render={() => (
              <UniversalComponent
                filename="Container_RegisterEmail"
                importer={
                  /* #__LOADABLE__ */ () =>
                    import('../container/RegisterEmail.js')
                }
              />
            )}
          />
        }
      />
      <RegExpRoute
        regExp={/^\/(?!age-verify)/i}
        element={
          <ModalView
            id={loginModal.AGE_VERIFICATION_OPTIONS}
            modalId={ModalId.LoginSignup.AgeVerificationPopup}
            render={() => (
              <UniversalComponent
                filename="Container_AgeVerificationOptions"
                importer={
                  /* #__LOADABLE__ */ () =>
                    import('../container/AgeVerificationOptions.js')
                }
              />
            )}
          />
        }
      />
      <ModalView
        id="Toastr"
        noScrollLock
        render={() => (
          <UniversalComponent
            filename="Container_Toastr"
            importer={
              /* #__LOADABLE__ */ () => import('../container/Toastr.js')
            }
          />
        )}
      />
      <ModalView
        id="InAppBrowserAlert"
        noScrollLock
        render={() => (
          <UniversalComponent
            filename="Container_InAppBrowserAlert"
            importer={
              /* #__LOADABLE__ */ () =>
                import('../container/InAppBrowserAlert.js')
            }
          />
        )}
      />
      <ModalView
        id="MobileAppDownloader"
        noScrollLock
        render={() => (
          <UniversalComponent
            filename="Container_DropDownPopup"
            importer={
              /* #__LOADABLE__ */ () =>
                import('../component/MobileAppDownloader.jsx')
            }
          >
            <UniversalComponent
              filename="Container_MobileAppDownloader"
              importer={
                /* #__LOADABLE__ */ () =>
                  import('../container/MobileAppDownloader.js')
              }
            />
          </UniversalComponent>
        )}
      />
      <ModalView
        id="MobileAppTutorial"
        render={() => (
          <UniversalComponent
            filename="Container_MobileAppTutorial"
            importer={
              /* #__LOADABLE__ */ () =>
                import('../container/MobileAppTutorial.js')
            }
          />
        )}
      />
      <UniversalComponent
        filename="Container_RegisterAndLoginModals"
        importer={
          /* #__LOADABLE__ */ () =>
            import('../container/RegisterAndLoginModals.js')
        }
      />
      <ModalView
        id="PwaSafariToast"
        modalId={ModalId.Popup.AddToHomeScreen}
        noScrollLock
        render={() => (
          <UniversalComponent
            filename="Container_PwaSafariToast"
            importer={
              /* #__LOADABLE__ */ () => import('../container/PwaSafariToast.js')
            }
          />
        )}
      />
      <ModalView
        id="DailyLoginRewardToastr"
        noScrollLock
        render={() => (
          <UniversalComponent
            filename="Container_DailyLoginRewardToastr"
            importer={
              /* #__LOADABLE__ */ () =>
                import('../container/DailyLoginRewardToastr.js')
            }
          />
        )}
      />
      <ModalView
        id="DailyLoginRewardDetail"
        noScrollLock
        render={() => (
          <UniversalComponent
            filename="Container_DailyLoginRewardDetail"
            importer={
              /* #__LOADABLE__ */ () =>
                import('../container/DailyLoginRewardDetail.js')
            }
          />
        )}
      />
      <ModalView
        id="PriceRefreshModal"
        render={() => (
          <UniversalComponent
            filename="Container_PriceRefreshModal"
            importer={
              /* #__LOADABLE__ */ () =>
                import('../container/PriceRefreshModal.js')
            }
          />
        )}
      />
      <ModalView
        id="PrepaidCodeConfirmModal"
        modalId={ModalId.Prepaid.PrepaidConfirmPopup}
        render={() => (
          <UniversalComponent
            filename="Container_PrepaidCodeConfirmModal"
            importer={
              /* #__LOADABLE__ */ () =>
                import('../container/PrepaidCodeConfirmModal.js')
            }
          />
        )}
      />
      <ModalView
        id="PrepaidCodeSuccessModal"
        modalId={ModalId.Prepaid.PrepaidSuccessPopup}
        render={() => (
          <UniversalComponent
            filename="Container_PrepaidCodeSuccessModal"
            importer={
              /* #__LOADABLE__ */ () =>
                import('../container/PrepaidCodeSuccessModal.js')
            }
          />
        )}
      />
      <ModalView
        modalId={ModalId.Chatroom.CpEventPage}
        id="ChatEventModal"
        render={() => (
          <UniversalComponent
            filename="Container_ChatEventModal"
            importer={
              /* #__LOADABLE__ */ () => import('../container/ChatEventModal.js')
            }
          />
        )}
      />
      <ModalView
        id="SetPasswordDirectorModal"
        modalId={ModalId.LoginSignup.NotSetPasswordPopup}
        render={() => (
          <UniversalComponent
            filename="Container_SetPasswordDirectorModal"
            importer={
              /* #__LOADABLE__ */ () =>
                import('../container/SetPasswordDirectorModal.js')
            }
          />
        )}
      />
      <ModalView
        id="YaboAddvalueConfirmModal"
        render={() => (
          <UniversalComponent
            filename="Container_YaboAddvalueConfirmModal"
            importer={
              /* #__LOADABLE__ */ () =>
                import('../container/YaboAddvalueConfirmModal.js')
            }
          />
        )}
      />
      <ModalView
        id="BlockUserModal"
        render={() => (
          <UniversalComponent
            filename="Container_BlockUserModal"
            importer={
              /* #__LOADABLE__ */ () => import('../container/BlockUserModal.js')
            }
          />
        )}
      />
      <ModalView
        modalId={ModalId.Popup.DrmCannotPlayPopup}
        id="DrmNotSupportAlert"
        render={() => (
          <UniversalComponent
            filename="Container_DrmNotSupportAlert"
            importer={
              /* #__LOADABLE__ */ () =>
                import('../container/DrmNotSupportAlert.js')
            }
          />
        )}
      />
      <ModalView
        modalId={ModalId.HamburgerMenu.Menu}
        id="HamburgerMenuModal"
        render={() => (
          <UniversalComponent
            filename="Container_HamburgerMenuModal"
            paddingFooterNavigationHeight={
              getIsIphoneXSeriesPWA()
                ? footerNavigationHeight + iPhoneXSeriesFooterPaddingInPWA
                : footerNavigationHeight
            }
            importer={
              /* #__LOADABLE__ */ () =>
                import('../container/HamburgerMenuModal.js')
            }
          />
        )}
      />
      <ModalView
        id="CopyConfirmModal"
        render={() => (
          <UniversalComponent
            filename="Container_CopyConfirmModal"
            importer={
              /* #__LOADABLE__ */ () =>
                import('../container/CopyConfirmModal.js')
            }
          />
        )}
      />
      <ModalView
        id="VerifyFreeUserModal"
        modalId={ModalId.Popup.CardVerify}
        render={() => (
          <UniversalComponent
            filename="Container_VerifyFreeUserModal"
            importer={
              /* #__LOADABLE__ */ () =>
                import('../container/VerifyFreeUserModal.js')
            }
          />
        )}
      />
      <ModalView
        id="SecurionpayAddNewCardModal"
        render={() => (
          <UniversalComponent
            filename="Container_SecurionpayAddNewCardModal"
            importer={
              /* #__LOADABLE__ */ () =>
                import('../container/SecurionpayAddNewCardModal.js')
            }
          />
        )}
      />
      <UniversalComponent
        filename="Container_LanguageMenuModalView"
        importer={
          /* #__LOADABLE__ */ () =>
            import('../container/LanguageMenuModalView.js')
        }
      />
      <ModalView
        modalId={ModalId.Join.ModalBecomeCreator}
        id="BecomeCreatorModal"
        render={() => (
          <UniversalComponent
            filename="Container_BecomeCreatorModal"
            importer={
              /* #__LOADABLE__ */ () =>
                import('../container/BecomeCreatorModal.js')
            }
          />
        )}
      />
      <ModalView
        modalId={ModalId.Join.ModalDownloadApp}
        id="DownloadCreatorModal"
        render={() => (
          <UniversalComponent
            filename="Container_DownloadCreatorModal"
            importer={
              /* #__LOADABLE__ */ () =>
                import('../container/DownloadCreatorModal.js')
            }
          />
        )}
      />
      <ModalView
        id="ImageCover"
        render={() => (
          <UniversalComponent
            filename="Container_ImageCover"
            importer={
              /* #__LOADABLE__ */ () => import('../container/ImageCover.js')
            }
          />
        )}
      />
      <ModalView
        id="BackpackModal"
        modalId={ModalId.Backpack.ModalBackpack}
        render={() => (
          <UniversalComponent
            filename="Container_BackpackModal"
            importer={
              /* #__LOADABLE__ */ () => import('../container/BackpackModal.js')
            }
          />
        )}
      />
      <ModalView
        id="PostSummaryModal"
        modalId={ModalId.Post.ModalPostSummaryDetail}
        render={() => (
          <UniversalComponent
            filename="Container_PostSummaryModal"
            importer={
              /* #__LOADABLE__ */ () =>
                import('../container/PostSummaryModal.js')
            }
          />
        )}
      />
      <ModalView
        id="PostLikesModal"
        modalId={ModalId.Post.ModalPostLikeDetail}
        render={() => (
          <UniversalComponent
            filename="Container_PostLikesModal"
            importer={
              /* #__LOADABLE__ */ () => import('../container/PostLikesModal.js')
            }
          />
        )}
      />
      <ModalView
        id="PostAssetFullscreenModal"
        render={() => (
          <UniversalComponent
            filename="Container_PostAssetFullscreenModal"
            importer={
              /* #__LOADABLE__ */ () =>
                import('../container/PostAssetFullscreenModal.js')
            }
          />
        )}
      />
      <UniversalComponent
        filename="Container_SubscriptionModals"
        importer={
          /* #__LOADABLE__ */ () => import('../container/SubscriptionModals.js')
        }
      />
      <UniversalComponent
        filename="Component_MessagePackModals"
        importer={
          /* #__LOADABLE__ */ () => import('../component/MessagePackModals.jsx')
        }
      />
      <ModalView
        modalId={ModalId.Popup.ModalMessageMore}
        id="MessageMoreModal"
        render={() => (
          <UniversalComponent
            filename="Container_MessageMoreModal"
            importer={
              /* #__LOADABLE__ */ () =>
                import('../container/MessageMoreModal.js')
            }
          />
        )}
      />
      <ModalView
        id="UploadMenuModal"
        modalId={ModalId.Upload.ModalCreate}
        render={() => (
          <UniversalComponent
            filename="Container_UploadMenuModal"
            importer={
              /* #__LOADABLE__ */ () =>
                import('../container/UploadMenuModal.js')
            }
          />
        )}
      />
      <ModalView
        id="UploadJobMoreModal"
        modalId={ModalId.Upload.ModalUploadMore}
        render={() => (
          <UniversalComponent
            filename="Container_UploadJobMoreModal"
            importer={
              /* #__LOADABLE__ */ () =>
                import('../container/UploadJobMoreModal.js')
            }
          />
        )}
      />
      <ModalView
        id="PostDraft"
        render={() => (
          <UniversalComponent
            filename="Container_PostDraft"
            importer={
              /* #__LOADABLE__ */ () => import('../container/PostDraft.js')
            }
          />
        )}
      />
      <ModalView
        modalId={ModalId.Notification.NotificationCenterList}
        id="NotificationCenterModal"
      >
        <UniversalComponent
          filename="Container_NotificationCenterModal"
          importer={
            /* #__LOADABLE__ */ () =>
              import('../container/NotificationCenterModal.js')
          }
        />
      </ModalView>
      <ModalView
        id={SEARCH_MODAL_ID}
        render={() => (
          <UniversalComponent
            filename="Container_SearchModal"
            importer={
              /* #__LOADABLE__ */ () => import('../container/SearchModal.js')
            }
          />
        )}
      />
      <ModalView
        id="PinConfirmModal"
        render={() => (
          <UniversalComponent
            filename="Container_PinConfirmModal"
            importer={
              /* #__LOADABLE__ */ () =>
                import('../container/PinConfirmModal.js')
            }
          />
        )}
      />
      <ModalView
        id="ClipConfirmModal"
        render={() => (
          <UniversalComponent
            filename="Container_ClipConfirmModal"
            modalId="ClipConfirmModal"
            importer={
              /* #__LOADABLE__ */ () =>
                import('../container/ClipConfirmModal.js')
            }
          />
        )}
      />
      <ModalView
        id="MessagePackUpsellingModal"
        render={() => (
          <UniversalComponent
            filename="Container_MessagePackUpsellingModal"
            modalId="MessagePackUpsellingModal"
            importer={
              /* #__LOADABLE__ */ () =>
                import('../container/MessagePackUpsellingModal.js')
            }
          />
        )}
      />
      <ModalView
        id="SettingWithoutSaveModal"
        render={() => (
          <UniversalComponent
            filename="Container_SettingWithoutSaveModal"
            importer={
              /* #__LOADABLE__ */ () =>
                import('../container/SettingWithoutSaveModal.js')
            }
          />
        )}
      />
      <ModalView id="HideMessageConfirmModal">
        <UniversalComponent
          filename="Container_HideMessageConfirmModal"
          modalId="HideMessageConfirmModal"
          importer={
            /* #__LOADABLE__ */ () =>
              import('../container/HideMessageConfirmModal.js')
          }
        />
      </ModalView>
      <ModalView
        id="PublishPostConfirmModal"
        modalId={ModalId.Post.ModalPublishAlert}
      >
        <UniversalComponent
          filename="Container_PublishPostConfirmModal"
          modalId="PublishPostConfirmModal"
          importer={
            /* #__LOADABLE__ */ () =>
              import('../container/PublishPostConfirmModal.js')
          }
        />
      </ModalView>
      <ModalView
        id="NotYetVerifiedAlert"
        modalId={ModalId.Bind.ModalNotYetVerified}
        noScrollLock
        render={() => (
          <UniversalComponent
            filename="Container_NotYetVerifiedAlert"
            importer={
              /* #__LOADABLE__ */ () =>
                import('../container/NotYetVerifiedAlert.js')
            }
          />
        )}
      />
      <ModalView modalId={ModalId.All.RatingModal} id="LivestreamRatingModal">
        <UniversalComponent
          filename="Container_LivestreamRatingModal"
          trackingModalId={ModalId.All.RatingModal}
          importer={
            /* #__LOADABLE__ */ () =>
              import('../container/LivestreamRatingModal.js')
          }
        />
      </ModalView>
      <ModalView
        id="IframeModal"
        render={() => (
          <UniversalComponent
            filename="Container_IframeModal"
            importer={
              /* #__LOADABLE__ */ () => import('../container/IframeModal.js')
            }
          />
        )}
      />
      <ModalView
        id="PhoneVerifyRewardModal"
        render={() => (
          <UniversalComponent
            filename="Container_PhoneVerifyRewardModal"
            modalId="PhoneVerifyRewardModal"
            importer={
              /* #__LOADABLE__ */ () =>
                import('../container/PhoneVerifyRewardModal.js')
            }
          />
        )}
      />
    </StyledApp>
  );
};

const StyledApp = styled.div``;

export default App;
